import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiClock, FiCalendar, FiArrowRight } from 'react-icons/fi';
import './blog.css';

const CATEGORIES = ['All', 'Technology', 'Career Guidance', 'Data Science', 'Data Analytics', 'Digital Marketing'];

const BlogList = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/$/, '');

  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/api/')) {
      return `${API_URL}${url}`;
    }
    return url;
  };

  useEffect(() => {
    fetchBlogs();
  }, [activeCategory]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let url = `${API_URL}/api/blogs`;
      const params = new URLSearchParams();
      if (activeCategory !== 'All') {
        params.append('category', activeCategory);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const res = await fetch(url);
      const json = await res.json();
      if (res.ok) {
        setBlogs(json.data || []);
      } else {
        setError(json.message || 'Failed to fetch blogs');
      }
    } catch (err) {
      setError('Connection to backend server failed.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Client-side search filtering
  const filteredBlogs = blogs.filter(blog => {
    const term = searchTerm.toLowerCase();
    return (
      blog.title.toLowerCase().includes(term) ||
      (blog.subtitle && blog.subtitle.toLowerCase().includes(term)) ||
      blog.content.toLowerCase().includes(term)
    );
  });

  const featuredBlog = filteredBlogs[0];
  const gridBlogs = filteredBlogs.slice(1);

  return (
    <section id="blog-list" className="blog__section">
      <div className="container blog__list-container">
        
        {/* HEADER HERO */}
        <div className="blog__header-intro">
          <h2>Insights & Resources</h2>
          <p className="blog__header-subtitle">
            Stay up to date with the latest industry news, career advice, technology trends, and updates from the SkillBridge community.
          </p>
        </div>

        {/* SEARCH AND FILTER BAR */}
        <div className="blog__search-filter-bar">
          <div className="blog__search-wrapper">
            <FiSearch className="blog__search-icon" />
            <input 
              type="text" 
              placeholder="Search articles..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="blog__search-input"
            />
          </div>

          <div className="blog__category-tabs">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                className={`blog__category-btn ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => {
                  setActiveCategory(cat);
                  setSearchTerm('');
                }}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {loading && <div className="blog__loading">Loading awesome articles...</div>}
        {error && <div className="blog__error">{error}</div>}

        {!loading && !error && filteredBlogs.length === 0 && (
          <div className="blog__empty">
            <h3>No articles found</h3>
            <p>Try matching different categories or search queries.</p>
          </div>
        )}

        {/* ARTICLES LAYOUT */}
        {!loading && !error && filteredBlogs.length > 0 && (
          <>
            {/* FEATURED POST */}
            {searchTerm === '' && featuredBlog && (
              <div 
                className="blog__featured-card"
                onClick={() => navigate(`/blog/${featuredBlog.slug || featuredBlog._id}`)}
              >
                <div className="blog__featured-img-wrap">
                  <img 
                    src={getFullImageUrl(featuredBlog.imageUrl) || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=1200&q=80'} 
                    alt={featuredBlog.title} 
                    className="blog__featured-img"
                  />
                </div>
                <div className="blog__featured-content">
                  <span className="blog__card-category">{featuredBlog.category}</span>
                  <h3 className="blog__featured-title">{featuredBlog.title}</h3>
                  <p className="blog__featured-subtitle-text">{featuredBlog.subtitle}</p>
                  
                  <div className="blog__featured-meta">
                    <span className="blog__meta-item"><FiClock /> {featuredBlog.readTime || '3 min read'}</span>
                    <span className="blog__meta-item"><FiCalendar /> {new Date(featuredBlog.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                  </div>

                  <div className="blog__featured-author">
                    <span>By {featuredBlog.authorName}</span>
                    <button className="blog__read-more-btn">
                      Read Article <FiArrowRight />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* BLOG GRID */}
            <div className="blog__grid">
              {(searchTerm === '' ? gridBlogs : filteredBlogs).map(blog => (
                <article 
                  key={blog._id} 
                  className="blog__card"
                  onClick={() => navigate(`/blog/${blog.slug || blog._id}`)}
                >
                  <div className="blog__card-img-wrap">
                    <img 
                      src={getFullImageUrl(blog.imageUrl) || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?auto=format&fit=crop&w=600&q=80'} 
                      alt={blog.title} 
                      className="blog__card-img"
                    />
                  </div>
                  <div className="blog__card-body">
                    <div className="blog__card-tag-row">
                      <span className="blog__card-category">{blog.category}</span>
                      <span className="blog__card-readtime"><FiClock /> {blog.readTime || '3 min read'}</span>
                    </div>
                    <h4 className="blog__card-title">{blog.title}</h4>
                    <p className="blog__card-excerpt">{blog.subtitle || blog.content.substring(0, 100) + '...'}</p>
                    
                    <div className="blog__card-footer">
                      <span className="blog__card-author">By {blog.authorName}</span>
                      <span className="blog__card-arrow"><FiArrowRight /></span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}

      </div>
    </section>
  );
};

export default BlogList;
